// Bitcoin (+ Zcash transparent) wallet provider. Each known wallet
// exposes a slightly different API, so we keep one adapter per provider
// rather than assume `requestAccounts()` everywhere. The omni-bridge
// flows we care about today are read-only on the BTC side (the user
// signs the deposit upstream); the connected wallet just provides an
// address so the operator can show "you're signed in as bc1q…".

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";

/** A `connect()` adapter for one specific Bitcoin wallet flavour. */
interface BtcAdapter {
  id: string;
  name: string;
  /** Is the wallet's injected global present right now? Re-runs each
   *  call because some providers replace themselves on disconnect. */
  detect: () => boolean;
  /** Pop the wallet's permission UI and return the first BTC address.
   *  Throws on user-cancel / unsupported error states. */
  connect: () => Promise<string | null>;
}

// ─── Per-provider adapters ──────────────────────────────────────────────

interface UnisatLike {
  requestAccounts: () => Promise<string[]>;
}
interface PhantomBtcLike {
  // Phantom's BTC provider returns `{ address, addressType, publicKey }`
  // from `requestAccount` (singular) instead of `requestAccounts`.
  requestAccount: () => Promise<{ address: string }>;
}
interface LeatherLike {
  // Leather (Hiro) exposes `request("getAddresses", …)` returning a
  // tagged shape; the BTC address is filtered out of an `addresses`
  // array (mainnet payment address).
  request: (
    method: string,
    params?: unknown,
  ) => Promise<{ result?: { addresses?: Array<{ symbol?: string; address?: string }> } }>;
}
interface XverseLike {
  // Xverse exposes `request("getAddresses", …)` similar to Leather.
  // (See https://docs.xverse.app/sats-connect for the canonical API.)
  request: (
    method: string,
    params?: unknown,
  ) => Promise<{ result?: { addresses?: Array<{ purpose?: string; address?: string }> } }>;
}

const ADAPTERS: BtcAdapter[] = [
  {
    id: "unisat",
    name: "Unisat",
    detect: () => !!(window as { unisat?: UnisatLike }).unisat,
    connect: async () => {
      const p = (window as { unisat?: UnisatLike }).unisat;
      if (!p) throw new Error("Unisat is not available");
      const accs = await p.requestAccounts();
      return accs[0] ?? null;
    },
  },
  {
    id: "okx",
    name: "OKX Wallet",
    detect: () => !!(window as { okxwallet?: { bitcoin?: UnisatLike } }).okxwallet?.bitcoin,
    connect: async () => {
      const p = (window as { okxwallet?: { bitcoin?: UnisatLike } }).okxwallet?.bitcoin;
      if (!p) throw new Error("OKX Wallet (Bitcoin) is not available");
      const accs = await p.requestAccounts();
      return accs[0] ?? null;
    },
  },
  {
    id: "phantom",
    name: "Phantom (BTC)",
    detect: () => !!(window as { phantom?: { bitcoin?: PhantomBtcLike } }).phantom?.bitcoin,
    connect: async () => {
      const p = (window as { phantom?: { bitcoin?: PhantomBtcLike } }).phantom?.bitcoin;
      if (!p) throw new Error("Phantom (BTC) is not available");
      const res = await p.requestAccount();
      return res?.address ?? null;
    },
  },
  {
    id: "leather",
    name: "Leather (Hiro)",
    detect: () => !!(window as { LeatherProvider?: LeatherLike }).LeatherProvider,
    connect: async () => {
      const p = (window as { LeatherProvider?: LeatherLike }).LeatherProvider;
      if (!p) throw new Error("Leather is not available");
      const res = await p.request("getAddresses");
      const addrs = res?.result?.addresses ?? [];
      // Prefer a `symbol === "BTC"` (modern Leather) entry over the first
      // one — guards against any future taproot/Stacks entries shipping
      // ahead of the BTC payment address.
      const btc = addrs.find((a) => a.symbol === "BTC") ?? addrs[0];
      return btc?.address ?? null;
    },
  },
  {
    id: "xverse",
    name: "Xverse",
    detect: () =>
      !!(window as { XverseProviders?: { BitcoinProvider?: XverseLike } }).XverseProviders
        ?.BitcoinProvider,
    connect: async () => {
      const p = (window as { XverseProviders?: { BitcoinProvider?: XverseLike } }).XverseProviders
        ?.BitcoinProvider;
      if (!p) throw new Error("Xverse is not available");
      const res = await p.request("getAddresses", {
        purposes: ["payment"],
        message: "OmniBridge Ops needs your Bitcoin payment address",
      });
      const addrs = res?.result?.addresses ?? [];
      const payment = addrs.find((a) => a.purpose === "payment") ?? addrs[0];
      return payment?.address ?? null;
    },
  },
];

interface BtcWalletState {
  account: string | null;
  available: boolean;
  providers: Array<{ id: string; name: string }>;
  connect: (providerId?: string) => Promise<string | null>;
  disconnect: () => Promise<void>;
}

const BtcWalletContext = createContext<BtcWalletState | null>(null);

export function BtcWalletProvider({ children }: PropsWithChildren) {
  const [account, setAccount] = useState<string | null>(null);
  const [availableIds, setAvailableIds] = useState<string[]>([]);
  const polling = useRef<number | null>(null);

  const probe = useCallback(() => {
    const detected = ADAPTERS.filter((a) => a.detect()).map((a) => a.id);
    setAvailableIds((prev) =>
      prev.length === detected.length && prev.every((id, i) => id === detected[i])
        ? prev
        : detected,
    );
  }, []);

  // Probe immediately. Then poll every 750ms — extensions inject at
  // various points in the page lifecycle; this loop catches a late
  // arrival without forcing a refresh. Pauses while the tab is hidden
  // (visibilitychange) so we don't burn cycles for an inactive operator.
  useEffect(() => {
    probe();
    const start = () => {
      if (polling.current !== null) return;
      polling.current = window.setInterval(probe, 750);
    };
    const stop = () => {
      if (polling.current === null) return;
      window.clearInterval(polling.current);
      polling.current = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };
    document.addEventListener("visibilitychange", onVisibility);
    start();
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [probe]);

  const connect = useCallback(async (providerId?: string) => {
    const adapter = providerId
      ? ADAPTERS.find((a) => a.id === providerId)
      : ADAPTERS.find((a) => a.detect());
    if (!adapter) throw new Error("No Bitcoin wallet detected.");
    const next = await adapter.connect();
    setAccount(next);
    return next;
  }, []);

  const disconnect = useCallback(async () => {
    // No standard `disconnect` across BTC wallets — Unisat/OKX hold a
    // dApp-side session forever, Phantom owns the prompt UX. Drop the
    // local state and let the wallet keep its session (reconnect is
    // one click).
    setAccount(null);
  }, []);

  const providersForUi = useMemo(
    () =>
      availableIds.flatMap((id) => {
        const a = ADAPTERS.find((q) => q.id === id);
        return a ? [{ id: a.id, name: a.name }] : [];
      }),
    [availableIds],
  );

  // Stable value identity so consumers' `useCallback` deps don't churn.
  const value = useMemo<BtcWalletState>(
    () => ({
      account,
      available: availableIds.length > 0,
      providers: providersForUi,
      connect,
      disconnect,
    }),
    [account, availableIds.length, providersForUi, connect, disconnect],
  );
  return <BtcWalletContext.Provider value={value}>{children}</BtcWalletContext.Provider>;
}

export function useBtcWallet(): BtcWalletState {
  const ctx = useContext(BtcWalletContext);
  if (!ctx) throw new Error("useBtcWallet must be inside <BtcWalletProvider>");
  return ctx;
}
